import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ModalActionBar from 'components/modal-action-bar';
import ContactCreation from 'components/users/form-contact-creation/contact-creation';
import React, { FC } from 'react';

import { ContactKind } from '../../types';
import { useModalCreateContact } from './modal-create-contact-hooks';

const useStyles = makeStyles((theme: any) => ({
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  title: { color: theme.palette.tab.offselected },
  closeButton: {
    fontSize: 16,
    '@media (hover: none) and (pointer: coarse)': {
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
  },
}));

type ModalCreateContactProps = {
  open: boolean;
  onClose: () => void;
  searchText?: string;
  contactKind?: ContactKind;
  fullScreen?: boolean;
};

export const ModalCreateContact: FC<ModalCreateContactProps> = ({
  open,
  onClose,
  searchText,
  contactKind,
  fullScreen = false,
}) => {
  const {
    t,
    history,
    created,
    createdId,
    setCreated,
    setCreatedId
  } = useModalCreateContact();
  const classes = useStyles();
  const handleNavigateEdit = () => createdId && history.push(`/contacts/${createdId}`);

  const handleClose = () => {
    setCreated(false);
    onClose();
  };

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="sm"
      fullScreen={fullScreen}
      onClose={handleClose}
      aria-labelledby="create-contact-title"
    >
      {/* Full-screen phones have no backdrop, so the title keeps an explicit close control. */}
      <DialogTitle className={classes.titleBar}>
        <Typography id="create-contact-title" variant="h6" component="h2" className={classes.title}>
          {t('users:create-contact')}
        </Typography>
        <IconButton
          aria-label={t('common:close')}
          className={classes.closeButton}
          onClick={handleClose}
          size="large"
        >
          <FontAwesomeIcon icon="times" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid container direction="column">
          <ContactCreation
            selectedKind={contactKind ?? 'Person'}
            onSuccess={({ _id }) => {
              setCreatedId(_id);
              setCreated(true);
            }}
            searchText={searchText}
          />
        </Grid>
      </DialogContent>

      {created && (
        <ModalActionBar
          onCancel={handleClose}
          cancelText={t('common:close')}
          submitType="button"
          submitText={t('contact:go-to-created-contact')}
          onSubmit={handleNavigateEdit}
        />
      )}
    </Dialog>
  );
};
